import { Component, OnInit, HostListener, effect, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';

import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDividerModule } from '@angular/material/divider';

import { AgGridModule } from 'ag-grid-angular';
import { ColDef, GridApi, GridReadyEvent, SelectionChangedEvent, IDatasource, IGetRowsParams, ICellRendererParams, ValueFormatterParams } from 'ag-grid-community';
import { defaultRowSelection } from '../../app/configs/ag-grid.config';

import { PaciensService } from '../../services/paciens.service';
import { PaciensVmForList } from '../../models/paciens.model';
import { Nem } from "../../models/nem.enum";
import { environment } from '../../app/environments/environment';
import { DebounceSignal } from '../../app/shared/debounce-signal';

@Component({
    selector: 'rendelo-paciens-list',
    imports: [
        CommonModule,
        MatButtonModule,
        MatIconModule,
        MatCardModule,
        MatFormFieldModule,
        MatInputModule,
        MatProgressSpinnerModule,
        MatSnackBarModule,
        MatSelectModule,
        MatTooltipModule,
        MatDividerModule,
        AgGridModule,
    ],
    templateUrl: './paciens-list.component.html',
    styleUrls: ['./paciens-list.component.scss']
})
export class PaciensListComponent implements OnInit {

    rowData: PaciensVmForList[] = [];

    searchQuery = signal('');
    debouncedSearchQuery = DebounceSignal(this.searchQuery, environment.debounceMs);

    colDefs: ColDef[] = [
        {
            field: 'nev',
            headerName: 'Név',
            sortable: true,
            cellRenderer: (params: ICellRendererParams) => this.boldTextCellRenderer(params)
        },
        {
            field: 'nem',
            headerName: 'Nem',
            width: 80,
            sortable: true,
            cellRenderer: (params: ICellRendererParams) => this.genderIconCellRenderer(params),
            cellStyle: { textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center' },
            headerClass: 'ag-header-cell-centered'
        },
        {
            field: 'szuletett',
            headerName: 'Született',
            sortable: true,
            valueFormatter: (params: ValueFormatterParams) => {
                if (!params.value) return '';
                const date = new Date(params.value);
                return date.toLocaleDateString('hu-HU');
            }
        },
        {
            field: 'tajszam',
            headerName: 'TAJ szám',
            sortable: true,
            cellRenderer: (params: ICellRendererParams) => this.boldTextCellRenderer(params)
        },
        {
            field: 'primaryTelefon',
            headerName: 'Telefonszám',
            sortable: true,
            cellRenderer: (params: ICellRendererParams) => this.boldTextCellRenderer(params)
        },
        {
            field: 'primaryEmail',
            headerName: 'Email',
            sortable: true,
            cellRenderer: (params: ICellRendererParams) => this.boldTextCellRenderer(params)
        }
    ];

    private gridApi?: GridApi;
    pageSize = environment.paging.defaultPageSize;
    private sortField = 'nev';

    selectedPaciens: PaciensVmForList | null = null;
    
    rowSelectionConfig = defaultRowSelection;

    constructor(
        private paciensService: PaciensService,
        private snackBar: MatSnackBar,
        private router: Router
    ) {
        effect(() => {
            const errorMessage = this.paciensService.error();
            if (errorMessage) {
                this.snackBar.open(errorMessage, 'Bezár', {

                    // TODO: Ki kell innen tenni az environment-be
                    duration: 10000,

                    panelClass: ['error-snackbar'],
                });
            }
        });

        effect(() => {
            const query = this.debouncedSearchQuery();
            console.log('Debounced search query changed:', query);
            if (this.gridApi) {
                this.gridApi.refreshInfiniteCache();
            }
        });
    }

    get loading() {
        return this.paciensService.loading;
    }

    get error() {
        return this.paciensService.error;
    }

    ngOnInit(): void {
    }

    onGridReady(params: GridReadyEvent) {
        this.gridApi = params.api;

        const datasource: IDatasource = {
            getRows: (params: IGetRowsParams) => {

                const startRow = params.startRow ?? 0;
                const pageNumber = Math.floor(startRow / this.pageSize);

                const searchQueryValue = this.debouncedSearchQuery();
                this.paciensService.loadPaciensek(
                    pageNumber,
                    this.pageSize,
                    searchQueryValue || undefined,
                    this.sortField
                );

                const checkData = () => {
                    const data = this.paciensService.paciensek();
                    const total = this.paciensService.totalItems();
                    const loading = this.paciensService.loading();

                    if (!loading && data.length > 0) {
                        params.successCallback(data, total);
                    } else if (!loading && data.length === 0) {
                        params.successCallback([], 0);
                    } else {
                        setTimeout(checkData, 50);
                    }
                };

                checkData();
            }
        };

        params.api.setGridOption('datasource', datasource);
    }

    onSelectionChanged(event: SelectionChangedEvent) {
        const selectedRows = event.api.getSelectedRows();
        this.selectedPaciens = selectedRows.length > 0 ? selectedRows[0] : null;

        const timestamp = new Date().toISOString();
        const currentPage = this.gridApi?.paginationGetCurrentPage() ?? -1;
    }



    addPaciens(): void {
        this.router.navigate(['/paciens/edit', 0]);
    }

    applyFilter(event: Event): void {
        const filterValue = (event.target as HTMLInputElement).value;
        this.searchQuery.set(filterValue);
    }

    boldTextCellRenderer(params: ICellRendererParams): string {
        const value = params.value || '';
        const searchText = this.debouncedSearchQuery();

        if (!searchText || !value) {
            return value;
        }

        const escapedSearch = searchText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const regex = new RegExp(`(${escapedSearch})`, 'gi');

        return value.replace(regex, '<strong>$1</strong>');
    }

    genderIconCellRenderer(params: ICellRendererParams): string {
        const nem = params.value as Nem;

        let icon = '';
        let color = '';
        let title = '';

        switch (nem) {
            case Nem.Ferfi:
                icon = '♂'; // Male symbol
                color = '#2196F3'; // Blue
                title = 'Férfi';
                break;
            case Nem.No:
                icon = '♀'; // Female symbol
                color = '#E91E63'; // Pink
                title = 'Nő';
                break;
            case Nem.Ismeretlen:
            default:
                // Return empty string for unknown gender
                return '';
        }

        return `<span style="color: ${color}; font-size: 20px; font-weight: bold;" title="${title}">${icon}</span>`;
    }

    viewDetails(): void {
        if (this.selectedPaciens) {
            this.router.navigate(['/paciens/detail', this.selectedPaciens.id]);
        }
    }

    editPaciens(): void {
        if (this.selectedPaciens) {
            this.router.navigate(['/paciens/edit', this.selectedPaciens.id]);
        }
    }

    deletePaciens(): void {
        if (this.selectedPaciens) {
            this.snackBar.open('Törlés - még nincs implementálva', 'Bezár', {
                duration: 3000,
            });
        }
    }

    @HostListener('document:keydown.escape')
    onEscapeKey(): void {
        if (this.selectedPaciens) {
            this.selectedPaciens = null;
            if (this.gridApi) {
                this.gridApi.deselectAll();
                this.gridApi.clearFocusedCell();

                const searchInput = document.querySelector('input[matInput]') as HTMLElement;
                if (searchInput) {
                    searchInput.focus();
                }
            }
        }
    }

    @HostListener('document:keydown.enter', ['$event'])
    onEnterKey(event: Event): void {
        if (this.selectedPaciens) {
            event.preventDefault();
            event.stopPropagation();
            this.viewDetails();
        }
    }

    @HostListener('document:keydown.delete', ['$event'])
    onDeleteKey(event: Event): void {
        const keyEvent = event as KeyboardEvent;
        if (this.selectedPaciens) {
            event.preventDefault();
            event.stopPropagation();
            this.deletePaciens();
        }
    }

    @HostListener('document:keydown.f12', ['$event'])
    onF12Key(event: Event): void {
        if (this.selectedPaciens) {
            event.preventDefault();
            event.stopPropagation();
            this.editPaciens();
        }
    }

    @HostListener('document:keydown.insert', ['$event'])
    onInsertKey(event: Event): void {
        const keyEvent = event as KeyboardEvent;
        if (this.isTypingInInput(keyEvent)) return;

        event.preventDefault();
        this.addPaciens();
    }

    @HostListener('document:keydown.arrowdown', ['$event'])
    onArrowDown(event: Event): void {
        const keyEvent = event as KeyboardEvent;
        if (this.isTypingInInput(keyEvent)) return;

        event.preventDefault();

        if (!this.gridApi) return;

        const selectedRows = this.gridApi.getSelectedRows();

        if (selectedRows.length === 0) {
            const currentPage = this.gridApi.paginationGetCurrentPage();
            const pageSize = this.gridApi.paginationGetPageSize();
            const firstRowIndex = currentPage * pageSize;
            this.selectRowByAbsoluteIndex(firstRowIndex);
        } else {
            const currentIndex = this.getSelectedRowIndex();
            if (currentIndex === null) return;

            const currentPage = this.gridApi.paginationGetCurrentPage();
            const pageSize = this.gridApi.paginationGetPageSize();
            const pageStartIndex = currentPage * pageSize;
            const pageEndIndex = pageStartIndex + pageSize - 1;

            if (currentIndex === pageEndIndex) {
                const totalPages = this.gridApi.paginationGetTotalPages();

                if (currentPage < totalPages - 1) {
                    this.gridApi.paginationGoToNextPage();
                    setTimeout(() => {
                        const newPage = this.gridApi!.paginationGetCurrentPage();
                        const pageSize = this.gridApi!.paginationGetPageSize();
                        const firstRowIndex = newPage * pageSize;
                        this.selectRowByAbsoluteIndex(firstRowIndex);
                    }, 200);
                }
            } else {
                this.selectRowByAbsoluteIndex(currentIndex + 1);
            }
        }
    }

    @HostListener('document:keydown.arrowup', ['$event'])
    onArrowUp(event: Event): void {
        const keyEvent = event as KeyboardEvent;
        if (this.isTypingInInput(keyEvent)) return;

        event.preventDefault();

        if (!this.gridApi) return;

        const selectedRows = this.gridApi.getSelectedRows();

        if (selectedRows.length === 0) {
            const currentPage = this.gridApi.paginationGetCurrentPage();
            const pageSize = this.gridApi.paginationGetPageSize();
            const lastRowIndex = currentPage * pageSize + pageSize - 1;
            this.selectRowByAbsoluteIndex(lastRowIndex);
        } else {
            const currentIndex = this.getSelectedRowIndex();
            if (currentIndex === null) return;

            const currentPage = this.gridApi.paginationGetCurrentPage();
            const pageSize = this.gridApi.paginationGetPageSize();
            const pageStartIndex = currentPage * pageSize;

            if (currentIndex === pageStartIndex) {
                if (currentPage > 0) {
                    this.gridApi.paginationGoToPreviousPage();
                    setTimeout(() => {
                        const newPage = this.gridApi!.paginationGetCurrentPage();
                        const pageSize = this.gridApi!.paginationGetPageSize();
                        const lastRowIndex = (newPage * pageSize) + pageSize - 1;
                        this.selectRowByAbsoluteIndex(lastRowIndex);
                    }, 200);
                }
            } else {
                this.selectRowByAbsoluteIndex(currentIndex - 1);
            }
        }
    }

    @HostListener('document:keydown.pagedown', ['$event'])
    onPageDown(event: Event): void {
        const keyEvent = event as KeyboardEvent;
        if (this.isTypingInInput(keyEvent)) return;

        event.preventDefault();

        if (!this.gridApi) return;

        const currentPage = this.gridApi.paginationGetCurrentPage();
        const totalPages = this.gridApi.paginationGetTotalPages();

        if (currentPage < totalPages - 1) {
            this.gridApi.paginationGoToNextPage();
            setTimeout(() => {
                const newPage = this.gridApi!.paginationGetCurrentPage();
                const pageSize = this.gridApi!.paginationGetPageSize();
                const firstRowIndex = newPage * pageSize;
                this.selectRowByAbsoluteIndex(firstRowIndex);
            }, 200);
        }
    }

    @HostListener('document:keydown.pageup', ['$event'])
    onPageUp(event: Event): void {
        const keyEvent = event as KeyboardEvent;
        if (this.isTypingInInput(keyEvent)) return;

        event.preventDefault();

        if (!this.gridApi) return;

        const currentPage = this.gridApi.paginationGetCurrentPage();

        if (currentPage > 0) {
            this.gridApi.paginationGoToPreviousPage();
            setTimeout(() => {
                const newPage = this.gridApi!.paginationGetCurrentPage();
                const pageSize = this.gridApi!.paginationGetPageSize();
                const lastRowIndex = (newPage * pageSize) + pageSize - 1;
                this.selectRowByAbsoluteIndex(lastRowIndex);
            }, 200);
        }
    }

    @HostListener('document:keydown.home', ['$event'])
    onHomeKey(event: Event): void {
        const keyEvent = event as KeyboardEvent;
        if (this.isTypingInInput(keyEvent)) return;

        event.preventDefault();

        if (!this.gridApi) return;

        this.gridApi.paginationGoToFirstPage();
        setTimeout(() => this.selectRowByAbsoluteIndex(0), 200);
    }

    @HostListener('document:keydown.end', ['$event'])
    onEndKey(event: Event): void {
        const keyEvent = event as KeyboardEvent;
        if (this.isTypingInInput(keyEvent)) return;

        event.preventDefault();

        if (!this.gridApi) return;

        this.gridApi.paginationGoToLastPage();
        setTimeout(() => {
            const totalRowCount = this.gridApi!.getDisplayedRowCount();
            if (totalRowCount > 0) {
                this.selectRowByAbsoluteIndex(totalRowCount - 1);
            }
        }, 200);
    }

    /**
     * Helper method to check if user is typing in an input field
     * Only prevents navigation for actual typing keys, not for navigation keys
     */
    private isTypingInInput(event: KeyboardEvent): boolean {
        const target = event.target as HTMLElement;
        if (target.tagName !== 'INPUT' && target.tagName !== 'TEXTAREA') {
            return false;
        }

        const navigationKeys = ['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', 'Escape', 'Insert'];
        return !navigationKeys.includes(event.key);
    }

    /**
     * Helper method to get the currently selected row index
     */
    private getSelectedRowIndex(): number | null {
        if (!this.gridApi) return null;

        const selectedRows = this.gridApi.getSelectedRows();
        if (selectedRows.length === 0) return null;

        const selectedRow = selectedRows[0];
        const displayedRowCount = this.gridApi.getDisplayedRowCount();

        for (let i = 0; i < displayedRowCount; i++) {
            const rowNode = this.gridApi.getDisplayedRowAtIndex(i);
            if (rowNode && rowNode.data === selectedRow) {
                return i;
            }
        }

        return null;
    }

    /**
     * Helper method to select a row by its display index (relative to current page)
     */
    private selectRowByIndex(index: number): void {
        if (!this.gridApi) return;

        const rowNode = this.gridApi.getDisplayedRowAtIndex(index);
        if (rowNode) {
            this.gridApi.deselectAll();
            rowNode.setSelected(true);
            this.gridApi.ensureIndexVisible(index);
        }
    }

    /**
     * Helper method to select a row by its absolute index in the infinite scroll model
     */
    private selectRowByAbsoluteIndex(absoluteIndex: number): void {
        if (!this.gridApi) return;

        const rowNode = this.gridApi.getDisplayedRowAtIndex(absoluteIndex);
        if (rowNode) {
            this.gridApi.deselectAll();
            rowNode.setSelected(true);
            this.gridApi.ensureIndexVisible(absoluteIndex);
        }
    }
}
